import { NAV_ITEMS, NavPersona, isHiddenForPersona } from '@/constants/nav';
import { IMenuItem } from '@/types/nav';
import {
  OverviewIcon,
  SignalsBarIcon,
  ChatbotIcon,
  AudiencesIcon,
  AiContextIcon,
} from './icons';

export interface CommandEntry {
  id: string;
  label: string;
  description?: string;
  /** Short subtitle shown inside page cards in the grid view (2–4 words) */
  shortDescription?: string;
  category: 'Go To' | 'Settings' | 'Actions' | 'Ask AI';
  keywords: string[];
  icon?: React.ComponentType<{ className?: string }>;
  action: { type: 'navigate'; href: string } | { type: 'ask_ai'; query: string };
}

/** Custom icon overrides for specific page cards (by href). */
const CUSTOM_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  '/dashboard': OverviewIcon as React.ComponentType<{ className?: string }>,
  '/signals': SignalsBarIcon as React.ComponentType<{ className?: string }>,
  '/bot': ChatbotIcon as React.ComponentType<{ className?: string }>,
  '/audiences': AudiencesIcon as React.ComponentType<{ className?: string }>,
  '/ai-context': AiContextIcon as React.ComponentType<{ className?: string }>,
};

interface WorkspaceContext {
  hasWorkspace: boolean;
  subscriptionTier: string | null;
  hasAccessToTier: (tiers: string[]) => boolean;
  activeWorkspace: {
    gtm_signal_enabled?: boolean;
    ai_seo_enabled?: boolean;
    outbound_enabled?: boolean;
  } | null;
  /** From navViewerPersona — the palette hides what the sidebar hides. */
  viewerPersona?: NavPersona | null;
}

/**
 * Additional navigation routes not in the sidebar NAV_ITEMS but accessible via URL.
 * These supplement the sidebar-derived entries to cover the full platform.
 */
/** Descriptions for sidebar nav items (keyed by href) to enrich AI context. */
const NAV_DESCRIPTIONS: Record<string, string> = {
  '/dashboard': 'Dashboard with key metrics, conversion rates, and pipeline health',
  '/analytics/activity': 'Timeline of account engagement events and touchpoints',
  '/signals': 'Real-time website visitor identification and intent signals',
  '/ai-seo': 'AI-powered SEO content generation and optimization',
  '/bot': 'Live chatbot training, configuration, and conversation history',
  '/tasks': 'Outbound task queue for email and LinkedIn outreach actions',
  '/audiences': 'Build target account lists and ICP segments for outreach',
  '/campaigns?tab=templates': 'Reusable outreach sequence templates and the people enrolled through them',
  '/ai-context': 'Configure AI knowledge base and company context for all AI features',
  '/settings': 'Workspace settings, integrations, team access, and billing',
};

/** Short subtitles shown on page cards in the grid (2–4 descriptive words). */
const NAV_SHORT_DESCRIPTIONS: Record<string, string> = {
  '/dashboard': 'Metrics, conversions, pipeline',
  '/analytics/activity': 'Engagement, Touchpoints',
  '/signals': 'Visitor intent signals',
  '/ai-seo': 'AI content generation',
  '/bot': 'Bot setup, Training, controls',
  '/tasks': 'Outreach task queue',
  '/audiences': 'Account segmentation',
  '/campaigns?tab=templates': 'Outreach sequence templates',
  '/ai-context': 'AI Knowledge base, settings',
  '/settings': 'Workspace configuration',
};

const EXTRA_NAV_ENTRIES: { label: string; href: string; description: string; shortDescription: string; keywords: string[]; requiresWorkspace?: boolean }[] = [
  { label: 'Revenue Analytics', href: '/analytics/revenue', description: 'Pipeline attribution and revenue impact reporting', shortDescription: 'Revenue, attribution', keywords: ['revenue', 'analytics', 'pipeline', 'attribution'], requiresWorkspace: true },
  { label: 'Workflows', href: '/workflows', description: 'Automated outreach sequences for email and LinkedIn', shortDescription: 'Outreach sequences', keywords: ['workflows', 'automation', 'sequences', 'outreach'], requiresWorkspace: true },
  { label: 'Workflow Builder', href: '/workflows/builder', description: 'Drag-and-drop editor to create multi-step outreach workflows', shortDescription: 'Build workflows', keywords: ['workflow', 'builder', 'editor', 'drag', 'drop'], requiresWorkspace: true },
  { label: 'Prospects', href: '/prospects', description: 'Enriched prospect database with contact details and firmographics', shortDescription: 'Contact database', keywords: ['prospects', 'leads', 'database', 'enrichment'], requiresWorkspace: true },
  { label: 'Website Visitors', href: '/vendor-leads', description: 'Deanonymized website visitors matched to companies and contacts', shortDescription: 'Visitor identification', keywords: ['website', 'visitors', 'vendor', 'leads', 'deanonymized'], requiresWorkspace: true },
  { label: 'AI Agents', href: '/agents', description: 'Configure and manage AI agents for automated tasks', shortDescription: 'AI automation', keywords: ['agents', 'ai', 'agent', 'configuration'], requiresWorkspace: true },
  { label: 'Internal Agents', href: '/internal-agents', description: 'Internal-use AI agents and tools for the team', shortDescription: 'Internal tools', keywords: ['internal', 'agents', 'tools'], requiresWorkspace: true },
  { label: 'Integrations', href: '/integrations', description: 'Connect CRM, email, and third-party tools', shortDescription: 'Connect tools', keywords: ['integrations', 'hubspot', 'salesforce', 'google', 'microsoft', 'connect'], requiresWorkspace: true },
  { label: 'Playbooks', href: '/playbooks', description: 'Step-by-step tutorials and onboarding guides', shortDescription: 'Tutorials, guides', keywords: ['playbooks', 'tutorials', 'onboarding', 'courses', 'guides'] },
  { label: 'HeyGen', href: '/heygen', description: 'AI-generated video avatars for personalized outreach', shortDescription: 'Video avatars', keywords: ['heygen', 'video', 'avatar', 'ai video'], requiresWorkspace: true },
];

const SETTINGS_ENTRIES: { label: string; tab: string; description: string; keywords: string[] }[] = [
  { label: 'Profile', tab: 'profile', description: 'Update your name, email, avatar, and personal preferences', keywords: ['profile', 'account', 'name', 'email', 'avatar'] },
  { label: 'Notifications', tab: 'notifications', description: 'Choose Slack or Microsoft Teams for task due alerts', keywords: ['notifications', 'alerts', 'tasks', 'due', 'slack', 'teams', 'reminders'] },
  { label: 'Security & API', tab: 'security-and-api', description: 'Manage API keys, MFA, SSO, and security settings', keywords: ['security', 'api', 'keys', 'password', 'tokens', 'mfa', 'sso'] },
  { label: 'Workspace', tab: 'workspace', description: 'Configure workspace name, logo, domain, and general settings', keywords: ['workspace', 'organization', 'logo', 'domain'] },
  { label: 'Team Access', tab: 'team-access', description: 'Invite members, manage roles, and control team permissions', keywords: ['team', 'members', 'access', 'invite', 'roles'] },
  { label: 'Billing & Usage', tab: 'billing-and-usage', description: 'View subscription plan, credit usage, and payment details', keywords: ['billing', 'usage', 'plan', 'subscription', 'payment', 'credits'] },
  { label: 'Integrations', tab: 'integrations', description: 'Connect HubSpot, Salesforce, Google, Microsoft, and other tools', keywords: ['integrations', 'connect', 'crm', 'hubspot', 'salesforce'] },
  { label: 'Champion Tracking', tab: 'champions', description: 'Configure job change tracking for key contacts at target accounts', keywords: ['champions', 'champion', 'configure', 'tracking', 'job changes'] },
  { label: 'Signals & Rules', tab: 'signals-and-rules', description: 'Define ICP scoring rules and signal assignment', keywords: ['signals', 'rules', 'scoring', 'icp'] },
  { label: 'Outreach Setup', tab: 'outreach', description: 'Task draft language, phone numbers, Pounce popup, and meeting routing', keywords: ['outreach', 'email', 'phone', 'pounce', 'meeting', 'routing', 'localization', 'language', 'spanish', 'english', 'task drafts'] },
  { label: 'Task Draft Language', tab: 'profile&section=task-draft-language', description: 'Set your personal task draft language preference for this workspace', keywords: ['task', 'draft', 'language', 'english', 'spanish', 'profile', 'sdr'] },
  { label: 'Website Embed', tab: 'mb-intent-client', description: 'Install the tracking script to identify website visitors', keywords: ['intent', 'client', 'tracking', 'script', 'embed', 'install'] },
  { label: 'Feedback', tab: 'feedback', description: 'Submit feedback or contact support', keywords: ['feedback', 'support', 'help'] },
];

const ACTION_ENTRIES: { label: string; href: string; description: string; keywords: string[] }[] = [
  { label: 'Create Audience', href: '/audiences', description: 'Build a new target account list or ICP segment', keywords: ['create', 'audience', 'new', 'segment'] },
  { label: 'Create Workflow', href: '/workflows/builder', description: 'Start a new multi-step outreach sequence', keywords: ['create', 'workflow', 'new', 'builder', 'sequence'] },
  { label: 'Connect HubSpot', href: '/settings?tab=integrations', description: 'Set up HubSpot CRM integration for syncing contacts and deals', keywords: ['connect', 'hubspot', 'crm', 'integration'] },
  { label: 'Connect Salesforce', href: '/settings?tab=integrations', description: 'Set up Salesforce CRM integration for syncing contacts and deals', keywords: ['connect', 'salesforce', 'crm', 'integration'] },
  { label: 'Invite Team Member', href: '/settings?tab=team-access', description: 'Add a new user to your workspace', keywords: ['invite', 'team', 'member', 'add', 'user'] },
  { label: 'Change Password', href: '/settings?tab=security-and-api', description: 'Update your account password', keywords: ['change', 'password', 'reset'] },
  { label: 'Enable MFA', href: '/settings?tab=security-and-api', description: 'Turn on two-factor authentication for your account', keywords: ['enable', 'mfa', 'two-factor', '2fa', 'security'] },
  { label: 'Install Tracking Script', href: '/settings?tab=mb-intent-client', description: 'Add the JavaScript snippet to your website to identify visitors', keywords: ['install', 'tracking', 'script', 'embed', 'pixel'] },
  { label: 'Train Chatbot', href: '/bot', description: 'Upload content and configure the chatbot knowledge base', keywords: ['train', 'chatbot', 'bot', 'configure'] },
  { label: 'Configure Pounce', href: '/settings?tab=outreach', description: 'Set up the on-site popup widget for visitor engagement', keywords: ['configure', 'pounce', 'popup', 'widget'] },
  { label: 'Set Up Meeting Routing', href: '/settings?tab=outreach', description: 'Configure calendar booking links and routing rules', keywords: ['meeting', 'routing', 'calendar', 'booking'] },
  { label: 'View Signals', href: '/signals', description: 'See real-time website visitor intent signals', keywords: ['view', 'signals', 'visitors'] },
  { label: 'Manage Workflows', href: '/workflows', description: 'View, edit, and manage outreach automation workflows', keywords: ['manage', 'workflows', 'automation'] },
];

const SUGGESTED_QUESTIONS: { label: string; query: string; keywords: string[] }[] = [
  { label: 'How to configure champions?', query: 'How to configure champions?', keywords: ['champions', 'configure'] },
  { label: 'How to integrate HubSpot?', query: 'How to integrate HubSpot?', keywords: ['hubspot', 'integrate', 'crm'] },
  { label: 'How to set up intent tracking?', query: 'How to set up intent tracking on my website?', keywords: ['intent', 'tracking', 'website'] },
  { label: 'How to create outreach sequences?', query: 'How to create outreach sequences?', keywords: ['outreach', 'sequences', 'email'] },
  { label: 'How do signals work?', query: 'How do signals and scoring rules work?', keywords: ['signals', 'scoring', 'rules'] },
];

function isFeatureEnabled(item: IMenuItem, workspace: WorkspaceContext['activeWorkspace']): boolean {
  if (!item.requiresFeature || !workspace) return true;
  if (item.requiresFeature === 'gtm_signal_enabled') return !!workspace.gtm_signal_enabled;
  if (item.requiresFeature === 'ai_seo_enabled') return !!workspace.ai_seo_enabled;
  if (item.requiresFeature === 'outbound_enabled') return workspace.outbound_enabled !== false;
  return true;
}

function flattenNavItems(items: IMenuItem[]): IMenuItem[] {
  const flat: IMenuItem[] = [];
  // A sidebar sub-nav entry is a page like any other: the palette offers what the nav offers.
  const push = (item: IMenuItem) => {
    flat.push(item);
    if (item.children) flat.push(...item.children);
  };
  for (const item of items) {
    if (item.isSectionHeader && item.sectionItems) {
      item.sectionItems.forEach(push);
    } else if (!item.isSectionHeader) {
      push(item);
    }
  }
  return flat;
}

export function buildCommandRegistry(ctx: WorkspaceContext): CommandEntry[] {
  const entries: CommandEntry[] = [];
  const addedHrefs = new Set<string>();

  const personaHiddenHrefs = new Set<string>();

  // Navigation entries from sidebar NAV_ITEMS
  for (const item of flattenNavItems(NAV_ITEMS)) {
    if (isHiddenForPersona(item, ctx.viewerPersona ?? null)) {
      personaHiddenHrefs.add(item.href);
      continue;
    }
    if (item.requiresWorkspace && !ctx.hasWorkspace) continue;
    if (item.allowedPlans && !ctx.hasAccessToTier(item.allowedPlans)) continue;
    if (!isFeatureEnabled(item, ctx.activeWorkspace)) continue;

    entries.push({
      id: `nav-${item.href}`,
      label: item.label,
      description: NAV_DESCRIPTIONS[item.href],
      shortDescription: NAV_SHORT_DESCRIPTIONS[item.href],
      category: 'Go To',
      keywords: [item.label.toLowerCase()],
      icon: CUSTOM_ICON_MAP[item.href] ?? (item.icon as CommandEntry['icon']),
      action: { type: 'navigate', href: item.href },
    });
    addedHrefs.add(item.href);
  }

  // Additional navigation routes not in sidebar
  for (const entry of EXTRA_NAV_ENTRIES) {
    if (addedHrefs.has(entry.href)) continue;
    if (entry.requiresWorkspace && !ctx.hasWorkspace) continue;

    entries.push({
      id: `nav-${entry.href}`,
      label: entry.label,
      description: entry.description,
      shortDescription: entry.shortDescription,
      category: 'Go To',
      keywords: entry.keywords,
      icon: CUSTOM_ICON_MAP[entry.href],
      action: { type: 'navigate', href: entry.href },
    });
    addedHrefs.add(entry.href);
  }

  // Settings entries
  for (const entry of SETTINGS_ENTRIES) {
    entries.push({
      id: `settings-${entry.tab}`,
      label: entry.label,
      description: entry.description,
      category: 'Settings',
      keywords: entry.keywords,
      action: { type: 'navigate', href: `/settings?tab=${entry.tab}` },
    });
  }

  // Action entries
  for (const entry of ACTION_ENTRIES) {
    if (personaHiddenHrefs.has(entry.href)) continue;
    entries.push({
      id: `action-${entry.label}`,
      label: entry.label,
      description: entry.description,
      category: 'Actions',
      keywords: entry.keywords,
      action: { type: 'navigate', href: entry.href },
    });
  }

  // Suggested AI questions
  for (const q of SUGGESTED_QUESTIONS) {
    entries.push({
      id: `ai-${q.query}`,
      label: q.label,
      category: 'Ask AI',
      keywords: q.keywords,
      action: { type: 'ask_ai', query: q.query },
    });
  }

  return entries;
}
